import { useCallback, useEffect, useState } from 'react';
import { Shield, Star, ChevronRight, LogOut, Truck, Wrench, Award, Phone, LayoutDashboard, HelpCircle, Headphones, X, ClipboardList, RefreshCw } from 'lucide-react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAppStore } from '../store';
import {
  activatePartnerCode,
  recalculateCart,
  getPublicBuildMeta,
  getProfileUi,
  DEFAULT_PROFILE_UI,
  DEFAULT_PROFILE_INFO_ITEMS,
  type PublicBuildMeta,
  type ProfileUiCopy,
  type ProfileInfoItem,
} from '../api';
import { WEBAPP_RELEASE_BLURB } from '../webappReleaseNotes';
import { useHaptic } from '../hooks/useHaptic';
import { ONVIZ_PROBE_WEBAPP_VERSION, forceReloadWebappBypassCache, reloadWebappForNewDeploy } from '../hooks/useWebappUpdateProbe';
import { GlassCard, SectionTitle } from '../components/Glass';
import { canAccessAdminUi, normalizeTelegramId } from '../adminAccess';
import { effectivePartnerDiscountPct } from '../lib/effectiveDiscount';
import { authCrmHintsFromApi } from '../crmAccess';
import { useAdminPendingQueueCounts } from '../hooks/useAdminPendingQueueCounts';

const ROLE_META: Record<string, { label: string; heroBg: string; glowColor: string; icon: string; desc: string }> = {
  client:  { label: 'Клиент',   heroBg: 'linear-gradient(135deg,rgba(99,179,237,0.12) 0%,rgba(10,25,47,0.95) 100%)', glowColor: 'rgba(99,179,237,0.2)',  icon: '◽', desc: 'Обычный покупатель' },
  partner: { label: 'Партнёр',  heroBg: 'linear-gradient(135deg,rgba(99,179,237,0.22) 0%,rgba(14,74,140,0.9) 100%)',  glowColor: 'rgba(99,179,237,0.35)', icon: '💠', desc: 'Партнёр Onviz' },
  manager: { label: 'Менеджер', heroBg: 'linear-gradient(135deg,rgba(246,173,85,0.2) 0%,rgba(10,25,47,0.95) 100%)', glowColor: 'rgba(246,173,85,0.35)', icon: '📋', desc: 'Команда Onviz' },
  measurer: { label: 'Замерщик', heroBg: 'linear-gradient(135deg,rgba(72,187,120,0.2) 0%,rgba(10,25,47,0.95) 100%)', glowColor: 'rgba(72,187,120,0.35)', icon: '📐', desc: 'Выезды и замеры Onviz' },
  designer: { label: 'Дизайнер', heroBg: 'linear-gradient(135deg,rgba(236,72,153,0.18) 0%,rgba(10,25,47,0.95) 100%)', glowColor: 'rgba(236,72,153,0.32)', icon: '🎨', desc: 'Проекты и подбор решений' },
  production: { label: 'Производство', heroBg: 'linear-gradient(135deg,rgba(160,174,192,0.22) 0%,rgba(10,25,47,0.95) 100%)', glowColor: 'rgba(160,174,192,0.35)', icon: '🏭', desc: 'Статусы заказов и этапы изготовления' },
  admin:   { label: 'Админ',    heroBg: 'linear-gradient(135deg,rgba(159,122,234,0.22) 0%,rgba(10,25,47,0.95) 100%)', glowColor: 'rgba(159,122,234,0.3)', icon: '◼', desc: 'Управление системой' },
};

/** Явный UTC без toLocaleString — на части WebView год/порядок полей отображались неверно. */
function formatUtcIsoExplicit(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${pad(d.getUTCDate())}.${pad(d.getUTCMonth() + 1)}.${d.getUTCFullYear()}, ${pad(d.getUTCHours())}:${pad(d.getUTCMinutes())}:${pad(d.getUTCSeconds())} UTC`;
}

/** Подпись сборки внизу профиля (константы с момента vite build / старта dev). */
const WEBAPP_BUILD_UI = (() => {
  const commit = __APP_COMMIT__;
  const deploy = __APP_DEPLOY_HINT__;
  const pkg = __APP_PKG_VERSION__;
  const line1 = `Mini App v${pkg} · git ${commit}${deploy ? ` · ${deploy}` : ''}`;
  const line2 = `собрано: ${formatUtcIsoExplicit(__APP_BUILT_AT__)}`;
  return { line1, line2 };
})();

function formatUtcShort(iso: string): string {
  return formatUtcIsoExplicit(iso);
}

const INFO_ICONS: Record<string, typeof Award> = {
  about: Award,
  warranty: Shield,
  delivery: Truck,
  install: Wrench,
  support: Phone,
  faq: HelpCircle,
  service: Headphones,
};

export default function ProfilePage() {
  const {
    firstName,
    role,
    userId,
    discountPct,
    clearAuth,
    setAuth,
    setDiscount,
    photoUrl,
    telegramId,
    authAdminHint,
  } = useAppStore();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const haptic = useHaptic();
  const [apiBuild, setApiBuild] = useState<PublicBuildMeta | null>(null);
  const [apiBuildErr, setApiBuildErr] = useState(false);
  const [apiBuildRefreshing, setApiBuildRefreshing] = useState(false);
  const [showCodeModal, setShowCodeModal] = useState(false);
  const [codeInput, setCodeInput] = useState('');
  const [codeLoading, setCodeLoading] = useState(false);
  const [codeResult, setCodeResult] = useState<{ label: string; emoji: string; discount: number } | null>(null);
  const [codeError, setCodeError] = useState('');
  const measurementIntent = searchParams.get('intent') === 'measurement';
  /** undefined — ещё не читали DOM; null — тега нет (старый index); string — из meta. */
  const [htmlShellCommit, setHtmlShellCommit] = useState<string | null | undefined>(undefined);
  /** Живой commit с Vercel `/version.json` (тот же origin, что и мини-апп). */
  const [vercelFileCommit, setVercelFileCommit] = useState<string | null>(null);
  const [vercelFileErr, setVercelFileErr] = useState(false);
  const [cacheBustBusy, setCacheBustBusy] = useState(false);
  const [profileCopy, setProfileCopy] = useState<ProfileUiCopy>({
    ...DEFAULT_PROFILE_UI,
    info_items: DEFAULT_PROFILE_INFO_ITEMS,
  });


  const handleActivate = async () => {
    if (!codeInput.trim()) return;
    setCodeLoading(true);
    setCodeError('');
    try {
      const data = await activatePartnerCode(codeInput.trim());
      setCodeResult({ label: data.label, emoji: data.emoji, discount: data.discount_pct });
      const twUid = normalizeTelegramId(window.Telegram?.WebApp?.initDataUnsafe?.user?.id);
      setAuth({
        role: data.role,
        token: data.access_token,
        telegramId: normalizeTelegramId(data.telegram_id) ?? twUid,
        ...authCrmHintsFromApi(data),
      });
      setDiscount(effectivePartnerDiscountPct(data.role, data.effective_discount_pct ?? data.discount_pct));
      haptic.success();
      try {
        const rec = await recalculateCart();
        setDiscount(Number(rec?.discount_pct ?? data.effective_discount_pct ?? data.discount_pct ?? 0));
      } catch (_) {
        /* cart may be empty */
      }
    } catch (e: any) {
      const msg = e?.response?.data?.detail || 'Не удалось активировать код. Проверьте данные и попробуйте снова.';
      setCodeError(msg);
      haptic.error();
    } finally {
      setCodeLoading(false);
    }
  };

  useEffect(() => {
    getProfileUi()
      .then((data) => setProfileCopy({
        ...DEFAULT_PROFILE_UI,
        ...data,
        info_items: data.info_items?.length ? data.info_items : DEFAULT_PROFILE_INFO_ITEMS,
      }))
      .catch(() => {
        setProfileCopy({ ...DEFAULT_PROFILE_UI, info_items: DEFAULT_PROFILE_INFO_ITEMS });
      });
  }, []);

  useEffect(() => {
    if (measurementIntent) {
      haptic.selection();
      navigate('/measurements?intent=measurement');
    }
  }, [measurementIntent, haptic, navigate]);

  useEffect(() => {
    const el = document.querySelector('meta[name="onviz-webapp-commit"]');
    setHtmlShellCommit(el ? (el.getAttribute('content') ?? '') : null);
  }, []);

  useEffect(() => {
    if (!canAccessAdminUi({ role, telegramId, serverAdminPanelHint: authAdminHint })) return;
    let stopped = false;
    fetch(`${window.location.origin}/version.json?ts=${Date.now()}`, {
      cache: 'no-store',
      headers: { Accept: 'application/json' },
    })
      .then((r) => {
        if (!r.ok) throw new Error('bad');
        return r.json() as Promise<{ commit?: string }>;
      })
      .then((j) => {
        const c = String(j?.commit ?? '').trim();
        if (!stopped) {
          setVercelFileCommit(c || null);
          setVercelFileErr(false);
        }
      })
      .catch(() => {
        if (!stopped) {
          setVercelFileCommit(null);
          setVercelFileErr(true);
        }
      });
    return () => {
      stopped = true;
    };
  }, [role, telegramId, authAdminHint]);

  const showAdminChromeEarly = canAccessAdminUi({ role, telegramId, serverAdminPanelHint: authAdminHint });
  const { pendingOrdersCount, pendingMeasurementsCount, pendingTotal } = useAdminPendingQueueCounts(
    Boolean(showAdminChromeEarly && userId),
  );

  const fetchApiBuildMeta = useCallback(
    async (opts?: { withHaptic?: boolean; showSpinner?: boolean; bustCache?: boolean }) => {
      if (!canAccessAdminUi({ role, telegramId, serverAdminPanelHint: authAdminHint })) return;
      if (opts?.showSpinner) setApiBuildRefreshing(true);
      try {
        const m = await getPublicBuildMeta({ bustCache: Boolean(opts?.bustCache) });
        setApiBuild(m);
        setApiBuildErr(false);
        if (opts?.withHaptic) haptic.success();
      } catch {
        setApiBuild(null);
        setApiBuildErr(true);
        if (opts?.withHaptic) haptic.error();
      } finally {
        if (opts?.showSpinner) setApiBuildRefreshing(false);
      }
    },
    [role, haptic, telegramId, authAdminHint],
  );

  useEffect(() => {
    if (!canAccessAdminUi({ role, telegramId, serverAdminPanelHint: authAdminHint })) {
      setApiBuild(null);
      setApiBuildErr(false);
      return;
    }
    let stopped = false;
    const load = async () => {
      try {
        const m = await getPublicBuildMeta();
        if (!stopped) {
          setApiBuild(m);
          setApiBuildErr(false);
        }
      } catch {
        if (!stopped) {
          setApiBuild(null);
          setApiBuildErr(true);
        }
      }
    };
    void load();
    const isDev = import.meta.env.DEV;
    const interval = isDev ? window.setInterval(() => void load(), 12_000) : 0;
    const onVis = () => {
      if (document.visibilityState === 'visible') void load();
    };
    document.addEventListener('visibilitychange', onVis);
    return () => {
      stopped = true;
      if (interval) window.clearInterval(interval);
      document.removeEventListener('visibilitychange', onVis);
    };
  }, [role, telegramId, authAdminHint]);

  const showAdminChrome = canAccessAdminUi({ role, telegramId, serverAdminPanelHint: authAdminHint });

  if (!userId) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen gap-6 px-8 text-center">
        <div className="w-24 h-24 rounded-full flex items-center justify-center"
          style={{ background: 'rgba(99,179,237,0.12)', border: '1px solid var(--border-glass)', boxShadow: '0 0 40px rgba(99,179,237,0.1)' }}>
          <span className="text-4xl">📱</span>
        </div>
        <div>
          <h2 className="text-xl font-bold mb-2" style={{ color: 'var(--text-1)' }}>Войдите через Telegram</h2>
          <p className="text-sm leading-relaxed" style={{ color: 'var(--text-2)' }}>
            Откройте этот магазин через бота Onviz в Telegram<br />для авторизации и полного доступа
          </p>
        </div>
      </div>
    );
  }

  const roleMeta = role ? ROLE_META[role] ?? ROLE_META.client : ROLE_META.client;
  const isPartner = role === 'partner' || role === 'admin';
  const effectiveDiscount = Math.max(0, Number(discountPct || 0));

  return (
    <div className="flex flex-col min-h-screen pb-20">

      {/* ── HERO ── */}
      <div className="px-4 pt-12 pb-6 relative"
        style={{ background: roleMeta.heroBg, borderBottom: '1px solid var(--border-glass)' }}>
        {/* Glow blob */}
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-64 h-32 rounded-full pointer-events-none"
          style={{ background: roleMeta.glowColor, filter: 'blur(48px)', opacity: 0.5 }} />

        <div className="relative flex items-center gap-4">
          <div className="w-16 h-16 rounded-2xl flex items-center justify-center text-3xl shrink-0 overflow-hidden"
            style={{ background: 'rgba(255,255,255,0.1)', backdropFilter: 'blur(12px)', border: '1px solid rgba(255,255,255,0.15)', boxShadow: `0 0 30px ${roleMeta.glowColor}` }}>
            {photoUrl ? (
              <img src={photoUrl} alt="avatar" className="w-full h-full object-cover" />
            ) : (
              roleMeta.icon
            )}
          </div>
          <div className="flex-1 min-w-0">
            <p className="font-bold text-white text-xl truncate">{firstName ?? 'Пользователь'}</p>
            <p className="text-xs mt-0.5" style={{ color: 'var(--text-2)' }}>{roleMeta.desc}</p>
          </div>
          <span className="text-[11px] font-bold px-3 py-1.5 rounded-full"
            style={{ background: 'rgba(255,255,255,0.12)', color: 'var(--cyan-bright)', border: '1px solid rgba(99,179,237,0.3)' }}>
            {roleMeta.label}
          </span>
        </div>

        {isPartner && effectiveDiscount > 0 ? (
          <div className="relative mt-4 rounded-2xl px-4 py-3 flex items-center gap-3"
            style={{ background: 'rgba(99,179,237,0.12)', border: '1px solid rgba(99,179,237,0.25)' }}>
            <Star size={18} color="#F5C518" fill="#F5C518" />
            <div>
              <p className="text-white font-semibold text-sm">{profileCopy.partner_active_title}</p>
              <p className="text-xs" style={{ color: 'var(--text-2)' }}>
                {profileCopy.partner_active_sub.includes('{pct}')
                  ? profileCopy.partner_active_sub.split('{pct}').map((part, i, arr) => (
                      <span key={i}>
                        {part}
                        {i < arr.length - 1 && (
                          <span style={{ color: '#68D391', fontWeight: 700 }}>{effectiveDiscount.toFixed(0)}%</span>
                        )}
                      </span>
                    ))
                  : profileCopy.partner_active_sub}
              </p>
            </div>
          </div>
        ) : !isPartner ? (
          <button
            onClick={() => { haptic.selection(); setShowCodeModal(true); setCodeResult(null); setCodeError(''); setCodeInput(''); }}
            className="w-full relative mt-4 rounded-2xl px-4 py-3 flex items-center gap-3 press"
            style={{ background: 'rgba(99,179,237,0.08)', border: '1px dashed rgba(99,179,237,0.4)' }}>
            <Star size={18} color="var(--cyan-bright)" />
            <div className="text-left">
              <p className="text-white font-semibold text-sm">{profileCopy.partner_banner_title}</p>
              <p className="text-xs" style={{ color: 'var(--text-2)' }}>{profileCopy.partner_banner_sub}</p>
            </div>
          </button>
        ) : null}
      </div>

      <div className="p-4 space-y-3">
        {/* Мои заказы */}
        <button
          type="button"
          onClick={() => { haptic.selection(); navigate('/orders'); }}
          className="press w-full flex items-center gap-3 px-4 py-4 rounded-2xl"
          style={{ background: 'rgba(99,179,237,0.12)', border: '1px solid var(--cyan-border)' }}
        >
          <div className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0"
            style={{ background: 'rgba(99,179,237,0.2)', border: '1px solid var(--cyan-border)' }}>
            <ClipboardList size={18} color="var(--cyan-bright)" />
          </div>
          <div className="flex-1 text-left">
            <p className="text-sm font-semibold" style={{ color: 'var(--text-1)' }}>{profileCopy.orders_label}</p>
            <p className="text-xs" style={{ color: 'var(--text-3)' }}>{profileCopy.orders_sub}</p>
          </div>
          <ChevronRight size={15} color="var(--cyan)" />
        </button>

        {/* Мои замеры */}
        <button
          type="button"
          onClick={() => { haptic.selection(); navigate('/measurements'); }}
          className="press w-full flex items-center gap-3 px-4 py-4 rounded-2xl"
          style={{ background: 'rgba(246,173,85,0.08)', border: '1px solid rgba(246,173,85,0.35)' }}
        >
          <div className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0"
            style={{ background: 'rgba(246,173,85,0.15)', border: '1px solid rgba(246,173,85,0.35)' }}>
            <LayoutDashboard size={18} color="#F6AD55" />
          </div>
          <div className="flex-1 text-left">
            <p className="text-sm font-semibold" style={{ color: 'var(--text-1)' }}>{profileCopy.measurements_label}</p>
            <p className="text-xs" style={{ color: 'var(--text-3)' }}>{profileCopy.measurements_sub}</p>
          </div>
          <ChevronRight size={15} color="#F6AD55" />
        </button>

        {/* Связаться с менеджером */}
        <button
          type="button"
          onClick={() => {
            haptic.selection();
            const href = profileCopy.manager_href || 'https://t.me/onvizmsk';
            if (window.Telegram?.WebApp?.openLink) window.Telegram.WebApp.openLink(href);
            else window.open(href, '_blank');
          }}
          className="press w-full flex items-center gap-3 px-4 py-4 rounded-2xl"
          style={{ background: 'rgba(72,187,120,0.08)', border: '1px solid rgba(72,187,120,0.35)' }}
        >
          <div className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0"
            style={{ background: 'rgba(72,187,120,0.15)', border: '1px solid rgba(72,187,120,0.35)' }}>
            <Phone size={18} color="#68D391" />
          </div>
          <div className="flex-1 text-left">
            <p className="text-sm font-semibold" style={{ color: 'var(--text-1)' }}>{profileCopy.manager_label}</p>
            <p className="text-xs" style={{ color: 'var(--text-3)' }}>{profileCopy.manager_sub}</p>
          </div>
          <ChevronRight size={15} color="#68D391" />
        </button>

        {showAdminChrome && (
          <button
            type="button"
            onClick={() => {
              haptic.selection();
              navigate(
                pendingOrdersCount > 0
                  ? '/admin?queue=pending'
                  : pendingMeasurementsCount > 0
                    ? '/admin?measurements=1'
                    : '/admin',
              );
            }}
            onMouseDown={() => haptic.selection()}
            className="press w-full flex items-center gap-3 px-4 py-3.5 rounded-2xl relative"
            style={{ background: 'rgba(159,122,234,0.12)', border: '1px solid rgba(159,122,234,0.3)' }}
          >
            {pendingTotal > 0 && (
              <span
                className="absolute top-2 right-2 text-white text-[10px] rounded-full min-w-[20px] h-5 px-1.5 flex items-center justify-center font-bold leading-none animate-pulse"
                style={{ background: '#FC814A', boxShadow: '0 0 12px rgba(252,129,74,0.55)' }}
              >
                {pendingTotal > 99 ? '99+' : pendingTotal}
              </span>
            )}
            <div className="w-9 h-9 rounded-xl flex items-center justify-center shrink-0"
              style={{ background: 'rgba(159,122,234,0.2)', border: '1px solid rgba(159,122,234,0.3)' }}>
              <LayoutDashboard size={16} color="#B794F4" />
            </div>
            <div className="flex-1 text-left">
              <p className="text-sm font-semibold flex items-center gap-2" style={{ color: 'var(--text-1)' }}>
                {profileCopy.admin_label}
                {pendingTotal > 0 && (
                  <span className="text-[10px] px-1.5 py-0.5 rounded-full animate-pulse" style={{ background: 'rgba(252,129,74,0.18)', color: '#FC814A', border: '1px solid rgba(252,129,74,0.35)' }}>
                    {pendingTotal > 99 ? '99+' : pendingTotal} новых
                  </span>
                )}
              </p>
              <p className="text-xs" style={{ color: 'var(--text-3)' }}>
                {pendingTotal > 0 ? profileCopy.admin_sub_pending : profileCopy.admin_sub}
              </p>
            </div>
            <ChevronRight size={15} color="var(--text-3)" />
          </button>
        )}

        {/* Info items */}
        <GlassCard className="overflow-hidden">
          <SectionTitle className="px-4 pt-4 pb-2">{profileCopy.section_title}</SectionTitle>
          {(profileCopy.info_items || DEFAULT_PROFILE_INFO_ITEMS).map(({ id, label, sub, href }: ProfileInfoItem) => {
            const Icon = INFO_ICONS[id] || Award;
            return (
            <button
              key={id}
              onClick={() => href && (window.Telegram?.WebApp?.openLink ? window.Telegram.WebApp.openLink(href) : window.open(href, '_blank'))}
              className="press w-full flex items-center gap-3 px-4 py-3.5 text-left"
              style={{ borderTop: '1px solid var(--border-subtle)' }}
            >
              <div className="w-9 h-9 rounded-xl flex items-center justify-center shrink-0"
                style={{ background: 'rgba(99,179,237,0.08)', border: '1px solid var(--border-subtle)' }}>
                <Icon size={16} color="var(--cyan)" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold" style={{ color: 'var(--text-1)' }}>{label}</p>
                <p className="text-xs mt-0.5 leading-tight line-clamp-2" style={{ color: 'var(--text-3)' }}>{sub}</p>
              </div>
              {href && <ChevronRight size={15} color="var(--text-3)" />}
            </button>
            );
          })}
        </GlassCard>

        <button
          onClick={() => { haptic.impact('medium'); clearAuth(); }}
          className="press w-full flex items-center justify-center gap-2 py-3.5 rounded-2xl font-semibold text-sm"
          style={{ background: 'rgba(252,129,74,0.08)', border: '1px solid rgba(252,129,74,0.2)', color: '#FC814A' }}
        >
          <LogOut size={16} /> Выйти
        </button>

        {showAdminChrome && (
          <GlassCard className="p-4">
            <SectionTitle className="mb-2">◈ Версия мини-аппа (отладка)</SectionTitle>
            <p className="text-xs leading-relaxed mb-2" style={{ color: 'var(--text-2)' }}>
              <span className="font-semibold" style={{ color: 'var(--text-1)' }}>Фронт</span> — то, что отдал Vercel (Root Directory{' '}
              <span className="font-mono">webapp</span>). <span className="font-semibold" style={{ color: 'var(--text-1)' }}>API</span> — отдельный деплой Railway (
              <span className="font-mono">backend</span>). SHA часто <span className="font-semibold" style={{ color: 'var(--text-1)' }}>различаются</span>: сервисы
              выкатываются независимо; совпадение git значит, что оба подтянули один и тот же коммит. Если на Vercel уже новый билд, а у вас старый JS в WebView, снизу появится плашка «Доступна новая версия» — нажмите «Обновить» (или кнопку ниже); это быстрее, чем вручную закрывать мини-апп.
            </p>
            <p className="text-xs leading-relaxed mb-2" style={{ color: 'var(--text-3)' }}>
              Бот сам подставляет <span className="font-mono">?v=…</span> из <span className="font-mono">/version.json</span> на Vercel (без ручных переменных). Нижняя reply-клавиатура подтягивает новые URL после{' '}
              <span className="font-mono">/start</span> или <span className="font-mono">/menu</span>. Ручной <span className="font-mono">WEBAPP_CACHE_BUST</span> — только переопределение авто.
            </p>
            <p className="text-[11px] font-mono mb-2 break-all" style={{ color: 'var(--text-3)' }}>
              Открыто: {typeof window !== 'undefined' ? `${window.location.origin}${window.location.pathname}` : '—'}
            </p>
            {htmlShellCommit === null && (
              <p className="text-xs mb-2 rounded-lg px-2 py-1.5" style={{ background: 'rgba(252,129,74,0.12)', color: '#F6AD55', border: '1px solid rgba(252,129,74,0.25)' }}>
                В HTML нет meta onviz-webapp-commit — оболочка страницы старая (кэш) или Vercel не выкатил последний main из папки webapp. Закройте мини-апп полностью; в дашборде Vercel — Redeploy.
              </p>
            )}
            {htmlShellCommit != null && htmlShellCommit !== '' && htmlShellCommit !== __APP_COMMIT__ && (
              <p className="text-xs mb-2 rounded-lg px-2 py-1.5" style={{ background: 'rgba(252,129,74,0.12)', color: '#F6AD55', border: '1px solid rgba(252,129,74,0.25)' }}>
                Рассинхрон: в HTML git <span className="font-mono">{htmlShellCommit}</span>, в JS <span className="font-mono">{__APP_COMMIT__}</span> — обновите страницу / закройте WebView.
              </p>
            )}
            {apiBuild && !apiBuildErr && apiBuild.git_sha_short && apiBuild.git_sha_short !== __APP_COMMIT__ && (
              <p className="text-xs mb-2 rounded-lg px-2 py-1.5" style={{ background: 'rgba(252,129,74,0.12)', color: '#F6AD55', border: '1px solid rgba(252,129,74,0.25)' }}>
                <span className="font-semibold" style={{ color: 'var(--text-1)' }}>Разные коммиты.</span> Фронт{' '}
                <span className="font-mono">{__APP_COMMIT__}</span>, API <span className="font-mono">{apiBuild.git_sha_short}</span>.
                «Перезагрузить мини-апп» не подтянет новый код с GitHub — только то, что уже выкатано на Vercel. Если API новее, проверьте GitHub Actions «Webapp Vercel Production» (секреты{' '}
                <span className="font-mono">VERCEL_TOKEN</span>, <span className="font-mono">VERCEL_ORG_ID</span>, <span className="font-mono">VERCEL_PROJECT_ID</span>) или сделайте Redeploy в панели Vercel. Ниже — «сброс кэша WebView»: помогает, когда на Vercel уже новый билд, а Telegram держит старый HTML.
              </p>
            )}
            <details className="text-xs mb-3 rounded-xl px-3 py-2" style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid var(--border-subtle)', color: 'var(--text-2)' }}>
              <summary className="cursor-pointer font-semibold select-none" style={{ color: 'var(--cyan-bright)' }}>
                Как читать строки с git и «собрано»
              </summary>
              <p className="mt-2 leading-relaxed pl-1" style={{ color: 'var(--text-3)' }}>
                Версия из package.json; git — короткий SHA (при сборке: из репозитория в webapp/.git-stamp, иначе VERCEL_GIT_COMMIT_SHA / CI); время — момент vite build; «деплой» — id выката Vercel,
                новый при каждом deploy даже без нового коммита. Локально без SHA показан local-… Сравните git фронта на двух телефонах после одного и того же деплоя Vercel.
              </p>
            </details>
            <button
              type="button"
              disabled={apiBuildRefreshing}
              onClick={() => {
                haptic.selection();
                void fetchApiBuildMeta({ withHaptic: true, showSpinner: true, bustCache: true });
              }}
              className="press w-full flex items-center justify-center gap-2 py-2.5 rounded-xl text-xs font-semibold mb-2 disabled:opacity-50 touch-manipulation"
              style={{ background: 'rgba(99,179,237,0.1)', border: '1px solid rgba(99,179,237,0.28)', color: 'var(--cyan-bright)' }}
            >
              <RefreshCw size={15} className={apiBuildRefreshing ? 'animate-spin shrink-0' : 'shrink-0'} aria-hidden />
              {apiBuildRefreshing ? 'Запрос к API…' : 'Обновить данные API (git / время)'}
            </button>
            <div className="grid grid-cols-1 gap-2 mb-3">
              <button
                type="button"
                onClick={() => {
                  haptic.selection();
                  window.dispatchEvent(new CustomEvent(ONVIZ_PROBE_WEBAPP_VERSION));
                }}
                className="press w-full py-2.5 rounded-xl text-xs font-semibold touch-manipulation"
                style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid var(--border-subtle)', color: 'var(--text-2)' }}
              >
                Проверить новый фронт (сравнить с /version.json)
              </button>
              <button
                type="button"
                onClick={() => {
                  haptic.selection();
                  reloadWebappForNewDeploy();
                }}
                className="press w-full py-2.5 rounded-xl text-xs font-semibold touch-manipulation"
                style={{ background: 'rgba(129,199,132,0.12)', border: '1px solid rgba(129,199,132,0.35)', color: '#B7F5CB' }}
              >
                Перезагрузить мини-апп сейчас
              </button>
              <button
                type="button"
                disabled={cacheBustBusy}
                onClick={() => {
                  haptic.selection();
                  setCacheBustBusy(true);
                  void forceReloadWebappBypassCache();
                }}
                className="press w-full py-2.5 rounded-xl text-xs font-semibold touch-manipulation disabled:opacity-50"
                style={{ background: 'rgba(159,122,234,0.14)', border: '1px solid rgba(159,122,234,0.35)', color: '#D6BCFA' }}
              >
                {cacheBustBusy ? 'Открываю новый URL…' : 'Сброс кэша WebView (новый URL)'}
              </button>
              <p className="text-[10px] leading-snug -mt-1 mb-1 px-0.5" style={{ color: 'var(--text-3)' }}>
                Добавляет <span className="font-mono">?_onviz_cb=…</span> к текущей странице — Telegram чаще заново грузит <span className="font-mono">index.html</span>. Если <span className="font-mono">/version.json</span> всё ещё с тем же git, что в JS, нужен деплой на Vercel, не кнопка.
              </p>
            </div>
            <div
              className="rounded-xl px-3 py-2.5 space-y-1 font-mono text-[11px] leading-relaxed"
              style={{ background: 'rgba(10,25,47,0.5)', border: '1px solid var(--border-subtle)' }}
            >
              <p style={{ color: 'var(--text-3)' }} className="text-[10px] uppercase tracking-wide mb-1">Фронт (этот билд)</p>
              <p style={{ color: 'var(--text-1)' }}>{WEBAPP_BUILD_UI.line1}</p>
              <p style={{ color: 'var(--text-2)' }}>{WEBAPP_BUILD_UI.line2}</p>
              {htmlShellCommit !== undefined && htmlShellCommit !== null && (
                <p style={{ color: 'var(--text-3)' }} className="text-[10px] pt-1">
                  HTML meta onviz-webapp-commit:{' '}
                  <span style={{ color: 'var(--cyan-bright)' }}>{htmlShellCommit || '(пусто)'}</span>
                </p>
              )}
              {vercelFileErr && (
                <p style={{ color: '#F6AD55' }} className="text-[10px] pt-1">
                  Не удалось загрузить <span className="font-mono">/version.json</span> с этого origin.
                </p>
              )}
              {vercelFileCommit && (
                <p style={{ color: 'var(--text-3)' }} className="text-[10px] pt-1">
                  <span className="font-mono">/version.json</span> на Vercel:{' '}
                  <span style={{ color: 'var(--cyan-bright)' }} className="font-mono">{vercelFileCommit}</span>
                  {vercelFileCommit === __APP_COMMIT__ && apiBuild?.git_sha_short && apiBuild.git_sha_short !== __APP_COMMIT__ && (
                    <span> — совпадает с JS; отстаёт только выкат фронта относительно API.</span>
                  )}
                </p>
              )}
              {apiBuildErr && (
                <p style={{ color: '#F6AD55' }} className="text-[10px] pt-1">
                  API: нет ответа /build-meta (проверьте сеть и VITE_BACKEND_URL)
                </p>
              )}
              {apiBuild && !apiBuildErr && (
                <>
                  <p style={{ color: 'var(--text-3)' }} className="text-[10px] uppercase tracking-wide pt-2 mb-1">
                    API (сервер)
                  </p>
                  <p style={{ color: 'var(--text-1)' }}>
                    git {apiBuild.git_sha_short ?? '—'}
                    {apiBuild.deploy_id_short ? ` · ${apiBuild.deploy_id_short}` : ''}
                  </p>
                  <p style={{ color: 'var(--text-2)' }} className="text-[10px]">
                    процесс: старт {formatUtcShort(apiBuild.process_boot_utc)} · сейчас {formatUtcShort(apiBuild.server_time_utc)}
                  </p>
                </>
              )}
            </div>
            <p className="text-xs mt-3 leading-relaxed" style={{ color: 'var(--text-2)' }}>
              <span className="font-semibold" style={{ color: 'var(--text-1)' }}>Последние изменения в этом выкате:</span> {WEBAPP_RELEASE_BLURB}
            </p>
            {import.meta.env.DEV && (
              <p className="text-[10px] mt-2" style={{ color: 'var(--text-3)' }}>
                Режим разработки: /build-meta опрашивается каждые 12 с и при возврате на вкладку.
              </p>
            )}
          </GlassCard>
        )}

        <div className="text-center pt-1 pb-2">
          {!showAdminChrome && (
            <p className="text-[10px] font-mono tracking-tight" style={{ color: 'var(--text-3)' }}>
              Сборка v{__APP_PKG_VERSION__} · {__APP_COMMIT__}
            </p>
          )}
          <p
            className="text-[8px] font-semibold tracking-[0.22em] mt-1 select-none"
            style={{ color: 'var(--text-3)', opacity: 0.42 }}
            aria-hidden
          >
            CREATED BY OKI
          </p>
        </div>
      </div>

      {/* Partner code modal */}
      {showCodeModal && (
        <div className="fixed inset-0 z-50 flex items-end justify-center" style={{ background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(12px)' }} onClick={() => { haptic.selection(); setShowCodeModal(false); }}>
          <div className="w-full max-w-lg rounded-t-3xl px-5 pt-5 pb-8" style={{ background: 'var(--sapphire-light)', border: '1px solid var(--border-glass)', borderBottom: 'none' }} onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <p className="font-bold text-base" style={{ color: 'var(--text-1)' }}>🔐 Партнёрский доступ</p>
              <button
                aria-label="Закрыть окно активации"
                onClick={() => { haptic.selection(); setShowCodeModal(false); }}
                style={{ color: 'var(--text-3)' }}
              >
                <X size={20} />
              </button>
            </div>

            {codeResult ? (
              <div className="text-center py-4">
                <div className="text-5xl mb-3">{codeResult.emoji}</div>
                <p className="font-black text-xl" style={{ color: 'var(--text-1)' }}>{codeResult.label}</p>
                <p className="text-sm mt-1" style={{ color: 'var(--text-2)' }}>Скидка <span style={{ color: '#68D391', fontWeight: 700 }}>{codeResult.discount.toFixed(0)}%</span> активирована!</p>
                <button
                  onClick={() => { haptic.selection(); setShowCodeModal(false); }}
                  className="btn-glow mt-5 w-full text-white font-bold py-3.5 rounded-2xl"
                >
                  Отлично!
                </button>
              </div>
            ) : (
              <>
                <p className="text-sm mb-3" style={{ color: 'var(--text-2)' }}>Введите код, предоставленный менеджером:</p>
                <input
                  type="text"
                  value={codeInput}
                  onChange={e => setCodeInput(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && handleActivate()}
                  placeholder="Введите код..."
                  className="input-glass w-full rounded-2xl px-4 py-3.5 font-mono tracking-widest text-center text-lg"
                  autoFocus
                />
                {codeError && <p className="text-xs mt-2 text-center" style={{ color: '#FC814A' }}>{codeError}</p>}
                <button
                  onClick={handleActivate}
                  disabled={codeLoading || !codeInput.trim()}
                  className="btn-glow press mt-4 w-full text-white font-bold py-3.5 rounded-2xl disabled:opacity-40 flex items-center justify-center gap-2"
                >
                  {codeLoading
                    ? <div className="w-5 h-5 border-2 border-t-transparent rounded-full animate-spin" style={{ borderColor: 'rgba(255,255,255,0.5)', borderTopColor: 'transparent' }} />
                    : 'Активировать'
                  }
                </button>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
